/**
 * WordOptimizer - Advanced word finding and placement strategy engine
 * 
 * Analyzes available letters and spelling bar modifiers to find optimal
 * word placements that maximize scoring potential while respecting game constraints.
 */

class WordOptimizer {
  /**
   * Find optimal word placement strategy
   *
   * @param {Array} letters - Current letters array (18 positions, null for empty)
   * @param {Array} spellingBar - Current spelling bar state (10 positions)
   * @param {Array} modifiers - Spelling bar modifiers (DL, TL, DW, TW, or null)
   * @param {Function} validateWord - Dictionary validation function
   * @param {Object} options - Additional options for optimization
   * @returns {Object} Optimization results with best word and alternatives
   */
  static findOptimalPlacement(letters, spellingBar, modifiers, validateWord, options = {}) {
    const {
      maxSuggestions = 5,
      minWordLength = 2,
      maxWordLength = 10,
      includePartialWords = false
    } = options;

    // Get available letters (non-null entries)
    const availableLetters = letters.filter(letter => letter !== null);
    
    if (availableLetters.length === 0) {
      return {
        bestWord: null,
        bestPlacement: null,
        bestScore: 0,
        alternatives: [],
        error: 'No letters available'
      };
    }

    // Check for existing letters in spelling bar
    const existingLetters = spellingBar
      .map((letter, index) => letter ? { letter, index } : null)
      .filter(item => item !== null);

    let allCandidates = [];

    // Generate word candidates
    if (existingLetters.length === 0) {
      // Fresh placement - generate all possible combinations
      allCandidates = this._generateFreshPlacements(
        availableLetters, 
        modifiers, 
        validateWord, 
        minWordLength, 
        maxWordLength
      );
    } else if (includePartialWords) {
      // Extend existing partial words
      allCandidates = this._generateExtensions(
        availableLetters,
        existingLetters,
        modifiers,
        validateWord,
        maxWordLength
      );
    }

    // Sort by score descending
    allCandidates.sort((a, b) => b.score - a.score);

    // Return results
    const best = allCandidates[0] || null;
    const alternatives = allCandidates.slice(1, maxSuggestions);

    return {
      bestWord: best?.word || null,
      bestPlacement: best?.placement || null,
      bestScore: best?.score || 0,
      alternatives: alternatives.map(candidate => ({
        word: candidate.word,
        placement: candidate.placement,
        score: candidate.score,
        breakdown: candidate.breakdown
      })),
      totalCandidatesEvaluated: allCandidates.length
    };
  }

  /**
   * Generate fresh word placements from available letters
   */
  static _generateFreshPlacements(availableLetters, modifiers, validateWord, minLength, maxLength) {
    const candidates = [];
    
    // Generate combinations of different lengths
    for (let length = minLength; length <= Math.min(maxLength, availableLetters.length, 9); length++) {
      const combinations = this._generateCombinations(availableLetters, length);
      
      for (const combination of combinations) {
        // Generate all permutations of this combination
        const permutations = this._generatePermutations(combination);
        
        for (const permutation of permutations) {
          const word = permutation.map(letter => letter.char).join('');
          
          // Validate word with dictionary
          if (validateWord(word)) {
            // Try all possible placements in spelling bar
            const placements = this._generatePlacements(permutation, modifiers);
            
            for (const placement of placements) {
              if (this._isValidPlacement(placement)) {
                const score = this._calculateScore(permutation, placement, modifiers);
                const breakdown = this._getScoreBreakdown(permutation, placement, modifiers);
                
                candidates.push({
                  word,
                  placement,
                  score,
                  breakdown,
                  letters: permutation
                });
              }
            }
          }
        }
      }
    }
    
    return candidates;
  }

  /**
   * Generate extensions for existing partial words
   */
  static _generateExtensions(availableLetters, existingLetters, modifiers, validateWord, maxLength) {
    const candidates = [];
    
    // Find contiguous sequence of existing letters
    const existingPositions = existingLetters.map(item => item.index).sort((a, b) => a - b);
    const isContiguous = existingPositions.every((pos, i) => 
      i === 0 || pos === existingPositions[i - 1] + 1
    );
    
    if (!isContiguous) {
      return candidates; // Can't extend non-contiguous sequences
    }
    
    const startPos = existingPositions[0];
    const endPos = existingPositions[existingPositions.length - 1];
    const existingWord = existingLetters
      .sort((a, b) => a.index - b.index)
      .map(item => item.letter.char)
      .join('');
    
    // Try adding letters before and after
    const maxAdditions = Math.min(availableLetters.length, maxLength - existingWord.length);
    
    for (let additions = 1; additions <= maxAdditions; additions++) {
      const addCombinations = this._generateCombinations(availableLetters, additions);
      
      for (const addLetters of addCombinations) {
        // Try adding before
        if (startPos >= additions) {
          const newWord = addLetters.map(l => l.char).join('') + existingWord;
          if (validateWord(newWord)) {
            const placement = this._createExtensionPlacement(
              addLetters, existingLetters, 'before', startPos - additions
            );
            if (this._isValidPlacement(placement)) {
              const score = this._calculateExtensionScore(addLetters, existingLetters, placement, modifiers);
              candidates.push({
                word: newWord,
                placement,
                score,
                breakdown: this._getExtensionBreakdown(addLetters, existingLetters, placement, modifiers),
                letters: [...addLetters, ...existingLetters.map(item => item.letter)]
              });
            }
          }
        }
        
        // Try adding after
        if (endPos + additions < 9) {
          const newWord = existingWord + addLetters.map(l => l.char).join('');
          if (validateWord(newWord)) {
            const placement = this._createExtensionPlacement(
              addLetters, existingLetters, 'after', endPos + 1
            );
            if (this._isValidPlacement(placement)) {
              const score = this._calculateExtensionScore(existingLetters, addLetters, placement, modifiers);
              candidates.push({
                word: newWord,
                placement,
                score,
                breakdown: this._getExtensionBreakdown(existingLetters, addLetters, placement, modifiers),
                letters: [...existingLetters.map(item => item.letter), ...addLetters]
              });
            }
          }
        }
      }
    }
    
    return candidates;
  }

  /**
   * Generate all combinations of specified length from available letters
   */
  static _generateCombinations(letters, length) {
    if (length === 0) return [[]];
    if (length > letters.length) return [];
    
    const combinations = [];
    
    function backtrack(start, current) {
      if (current.length === length) {
        combinations.push([...current]);
        return;
      }
      
      for (let i = start; i < letters.length; i++) {
        current.push(letters[i]);
        backtrack(i + 1, current);
        current.pop();
      }
    }
    
    backtrack(0, []);
    return combinations;
  }

  /**
   * Generate all permutations of a letter combination
   */
  static _generatePermutations(letters) {
    if (letters.length <= 1) return [letters];
    
    const permutations = [];
    
    for (let i = 0; i < letters.length; i++) {
      const current = letters[i];
      const remaining = [...letters.slice(0, i), ...letters.slice(i + 1)];
      const subPermutations = this._generatePermutations(remaining);
      
      for (const subPerm of subPermutations) {
        permutations.push([current, ...subPerm]);
      }
    }
    
    return permutations;
  }

  /**
   * Generate all valid placements for a word in the spelling bar
   */
  static _generatePlacements(letters, modifiers) {
    const placements = [];
    const wordLength = letters.length;

    // Try each starting position
    for (let start = 0; start <= 10 - wordLength; start++) {
      const placement = Array(10).fill(null);

      for (let i = 0; i < wordLength; i++) {
        placement[start + i] = {
          letter: letters[i],
          position: start + i
        };
      }

      placements.push(placement);
    }

    return placements;
  }

  /**
   * Check if placement is valid (mandatory center position filled)
   */
  static _isValidPlacement(placement) {
    return placement[4] !== null; // Center position must be filled
  }

  /**
   * Calculate total score for a word placement
   */
  static _calculateScore(letters, placement, modifiers) {
    let letterScore = 0;
    let wordMultiplier = 1;
    
    for (let i = 0; i < placement.length; i++) {
      if (placement[i]) {
        const letter = placement[i].letter;
        let points = letter.points;
        const modifier = modifiers[i];
        
        // Apply letter multipliers
        if (modifier === 'DL') points *= 2;
        else if (modifier === 'TL') points *= 3;
        
        letterScore += points;
        
        // Accumulate word multipliers
        if (modifier === 'DW') wordMultiplier *= 2;
        else if (modifier === 'TW') wordMultiplier *= 3;
      }
    }
    
    return letterScore * wordMultiplier;
  }

  /**
   * Get detailed score breakdown
   */
  static _getScoreBreakdown(letters, placement, modifiers) {
    const breakdown = {
      letterScores: [],
      baseScore: 0,
      letterMultipliers: [],
      wordMultipliers: [],
      finalScore: 0
    };
    
    let wordMultiplier = 1;
    
    for (let i = 0; i < placement.length; i++) {
      if (placement[i]) {
        const letter = placement[i].letter;
        const basePoints = letter.points;
        const modifier = modifiers[i];
        let finalPoints = basePoints;
        
        if (modifier === 'DL') {
          finalPoints *= 2;
          breakdown.letterMultipliers.push({ position: i, type: 'DL', multiplier: 2 });
        } else if (modifier === 'TL') {
          finalPoints *= 3;
          breakdown.letterMultipliers.push({ position: i, type: 'TL', multiplier: 3 });
        }
        
        if (modifier === 'DW') {
          wordMultiplier *= 2;
          breakdown.wordMultipliers.push({ position: i, type: 'DW', multiplier: 2 });
        } else if (modifier === 'TW') {
          wordMultiplier *= 3;
          breakdown.wordMultipliers.push({ position: i, type: 'TW', multiplier: 3 });
        }
        
        breakdown.letterScores.push({
          position: i,
          letter: letter.char,
          basePoints,
          finalPoints,
          modifier
        });
        
        breakdown.baseScore += finalPoints;
      }
    }
    
    breakdown.finalScore = breakdown.baseScore * wordMultiplier;
    breakdown.totalWordMultiplier = wordMultiplier;
    
    return breakdown;
  }

  /**
   * Create placement for word extensions
   */
  static _createExtensionPlacement(newLetters, existingLetters, direction, startPos) {
    const placement = Array(9).fill(null);
    
    if (direction === 'before') {
      // Place new letters before existing ones
      for (let i = 0; i < newLetters.length; i++) {
        placement[startPos + i] = {
          letter: newLetters[i],
          position: startPos + i
        };
      }
      // Keep existing letters in their positions
      for (const item of existingLetters) {
        placement[item.index] = {
          letter: item.letter,
          position: item.index
        };
      }
    } else {
      // Keep existing letters in their positions
      for (const item of existingLetters) {
        placement[item.index] = {
          letter: item.letter,
          position: item.index
        };
      }
      // Place new letters after existing ones
      for (let i = 0; i < newLetters.length; i++) {
        placement[startPos + i] = {
          letter: newLetters[i],
          position: startPos + i
        };
      }
    }
    
    return placement;
  }

  /**
   * Calculate score for word extensions
   */
  static _calculateExtensionScore(existingLetters, newLetters, placement, modifiers) {
    return this._calculateScore([...existingLetters, ...newLetters], placement, modifiers);
  }

  /**
   * Get score breakdown for extensions
   */
  static _getExtensionBreakdown(existingLetters, newLetters, placement, modifiers) {
    return this._getScoreBreakdown([...existingLetters, ...newLetters], placement, modifiers);
  }

  /**
   * Quick analysis for real-time suggestions during gameplay
   */
  static getQuickSuggestions(letters, spellingBar, modifiers, validateWord, maxResults = 3) {
    const options = {
      maxSuggestions: maxResults,
      minWordLength: 3,
      maxWordLength: 7, // Limit for performance
      includePartialWords: spellingBar.some(slot => slot !== null)
    };
    
    return this.findOptimalPlacement(letters, spellingBar, modifiers, validateWord, options);
  }
}

export default WordOptimizer;
